/**
 * AppShell - Unified Layout for Routed Applications
 *
 * Provides consistent layout structure for apps with multiple routes (Admin, Developer, etc.)
 * Includes:
 * - Collapsible sidebar navigation
 * - Dynamic app header (context-managed)
 * - Main content area with router outlet
 * - Bundle mode auto-detection and bottom navigation
 * - Responsive mobile behavior
 *
 * Usage:
 * ```tsx
 * import { AppShell } from '@room/shared-ui';
 * import { BrowserRouter, Routes, Route } from 'react-router-dom';
 *
 * export function App() {
 *   return (
 *     <RootLayout appName="Admin">
 *       <BrowserRouter basename="/admin">
 *         <AppShell
 *           appName="Admin"
 *           sidebarItems={navigationItems}
 *           showSidebarInBundle={false}
 *         >
 *           <Routes>
 *             <Route path="/" element={<HomePage />} />
 *             <Route path="/settings" element={<SettingsPage />} />
 *           </Routes>
 *         </AppShell>
 *       </BrowserRouter>
 *     </RootLayout>
 *   );
 * }
 * ```
 */

import { ReactNode, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppHeader, type AppHeaderConfig } from '../components/organisms/AppHeader/AppHeader';
import { AppHeaderProvider } from './AppHeaderContext';
import { NavigationProvider } from '../contexts/NavigationContext';
import { BottomNavigationBar } from '../components/BottomNavigationBar';
import { isBundleMode } from '../utils/bundleMode';
import './AppShell.css';

export interface SidebarNavItem {
  id: string;
  label: string;
  icon?: string;
  path: string;
  badge?: number | string;
  visible?: boolean;
  children?: SidebarNavItem[];
}

export interface AppShellProps {
  /**
   * Application name displayed in header
   */
  appName: string;

  /**
   * Sidebar navigation items
   */
  sidebarItems?: SidebarNavItem[];

  /**
   * Route outlet content (React Router Routes)
   */
  children: ReactNode;

  /**
   * Show sidebar in bundle mode
   * @default true
   */
  showSidebarInBundle?: boolean;

  /**
   * Show header in bundle mode
   * @default true (controlled by config in implementation)
   */
  showHeaderInBundle?: boolean;

  /**
   * Show bottom navigation bar in bundle mode
   * @default true
   */
  showBottomNavInBundle?: boolean;

  /**
   * Initial sidebar collapsed state
   * @default false on desktop, true on mobile
   */
  initialSidebarCollapsed?: boolean;

  /**
   * Sidebar position
   * @default 'left'
   */
  sidebarPosition?: 'left' | 'right';

  /**
   * Custom header render function
   * If provided, this overrides default header rendering
   */
  renderHeader?: (config: AppHeaderConfig | null) => ReactNode;

  /**
   * Map of route paths to page titles
   * Used for default header title if not set via context
   */
  routeTitles?: Record<string, string>;

  /**
   * Footer content (optional)
   */
  footer?: ReactNode;
}

/**
 * AppShell component - unified layout for routed applications
 */
export function AppShell({
  appName,
  sidebarItems = [],
  children,
  showSidebarInBundle = true,
  showHeaderInBundle = true,
  showBottomNavInBundle = true,
  initialSidebarCollapsed,
  sidebarPosition = 'left',
  renderHeader,
  routeTitles = {},
  footer,
}: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isRunningInBundle = isBundleMode();

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (initialSidebarCollapsed !== undefined) return initialSidebarCollapsed;
    // Auto-collapse on mobile
    return window.innerWidth < 900;
  });

  // Header state managed via context
  const [headerConfig, setHeaderConfig] = useState<AppHeaderConfig | null>(null);

  // Determine active sidebar item based on current route
  const activeItemId = useMemo(() => {
    const findActiveItem = (items: SidebarNavItem[]): string | null => {
      for (const item of items) {
        if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
          return item.id;
        }
        if (item.children) {
          const childMatch = findActiveItem(item.children);
          if (childMatch) return childMatch;
        }
      }
      return null;
    };
    return findActiveItem(sidebarItems);
  }, [location.pathname, sidebarItems]);

  // Resolve header title from context or route map
  const resolvedTitle = useMemo(() => {
    if (headerConfig?.title) return headerConfig.title;
    return routeTitles[location.pathname] || appName;
  }, [headerConfig?.title, location.pathname, routeTitles, appName]);

  // Determine visibility based on bundle mode
  const shouldShowSidebar = !isRunningInBundle || showSidebarInBundle;
  const shouldShowHeader = !isRunningInBundle || showHeaderInBundle;
  const shouldShowBottomNav = isRunningInBundle && showBottomNavInBundle;

  // Handle sidebar item click
  const handleSidebarItemClick = (item: SidebarNavItem) => {
    navigate(item.path);
    // Auto-collapse sidebar on mobile after navigation
    if (window.innerWidth < 900) {
      setSidebarCollapsed(true);
    }
  };

  // Render sidebar
  const renderSidebar = () => {
    if (!shouldShowSidebar || sidebarItems.length === 0) return null;

    return (
      <aside
        className={`app-shell__sidebar app-shell__sidebar--${sidebarPosition} ${
          sidebarCollapsed ? 'app-shell__sidebar--collapsed' : ''
        }`}
      >
        <nav className="app-shell__sidebar-nav">
          {sidebarItems.map((item) => {
            if (item.visible === false) return null;

            const isActive = activeItemId === item.id;

            return (
              <div key={item.id} className="app-shell__sidebar-item-container">
                <button
                  type="button"
                  className={`app-shell__sidebar-item ${
                    isActive ? 'app-shell__sidebar-item--active' : ''
                  }`}
                  onClick={() => handleSidebarItemClick(item)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.icon && (
                    <span className="app-shell__sidebar-item-icon">{item.icon}</span>
                  )}
                  {!sidebarCollapsed && (
                    <>
                      <span className="app-shell__sidebar-item-label">{item.label}</span>
                      {item.badge && (
                        <span className="app-shell__sidebar-item-badge">{item.badge}</span>
                      )}
                    </>
                  )}
                </button>

                {/* Render children if expanded and has children */}
                {!sidebarCollapsed && item.children && item.children.length > 0 && (
                  <div className="app-shell__sidebar-children">
                    {item.children.map((child) => {
                      if (child.visible === false) return null;
                      const isChildActive = activeItemId === child.id;
                      return (
                        <button
                          key={child.id}
                          type="button"
                          className={`app-shell__sidebar-item app-shell__sidebar-item--child ${
                            isChildActive ? 'app-shell__sidebar-item--active' : ''
                          }`}
                          onClick={() => handleSidebarItemClick(child)}
                          aria-current={isChildActive ? 'page' : undefined}
                        >
                          {child.icon && (
                            <span className="app-shell__sidebar-item-icon">{child.icon}</span>
                          )}
                          <span className="app-shell__sidebar-item-label">{child.label}</span>
                          {child.badge && (
                            <span className="app-shell__sidebar-item-badge">{child.badge}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar toggle button */}
        <button
          type="button"
          className="app-shell__sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '›' : '‹'}
        </button>
      </aside>
    );
  };

  // Render header
  const renderHeaderContent = () => {
    if (!shouldShowHeader) return null;

    if (renderHeader) {
      return renderHeader(headerConfig);
    }

    return (
      <AppHeader
        title={resolvedTitle}
        subtitle={headerConfig?.subtitle}
        actions={headerConfig?.actions}
        showMenu={headerConfig?.showMenu ?? (shouldShowSidebar && sidebarItems.length > 0)}
        onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        leftSlot={headerConfig?.leftSlot}
        centerSlot={headerConfig?.centerSlot}
        rightSlot={headerConfig?.rightSlot}
        secondaryRow={headerConfig?.secondaryRow}
        backAction={headerConfig?.backAction}
        variant={headerConfig?.variant}
        className={headerConfig?.className}
        sticky={headerConfig?.sticky}
      />
    );
  };

  return (
    <AppHeaderProvider value={{ setHeaderConfig }}>
      <NavigationProvider defaultVisible={shouldShowBottomNav}>
        <div
          className={`app-shell ${
            shouldShowSidebar ? 'app-shell--with-sidebar' : ''
          } ${
            sidebarCollapsed ? 'app-shell--sidebar-collapsed' : ''
          } ${
            shouldShowBottomNav ? 'app-shell--with-bottom-nav' : ''
          }`}
        >
          {renderSidebar()}

          <div className="app-shell__main">
            {renderHeaderContent()}

            <main className="app-shell__content">
              {children}
            </main>

            {footer && <footer className="app-shell__footer">{footer}</footer>}
          </div>

          {shouldShowBottomNav && (
            <div className="app-shell__bottom-nav">
              <BottomNavigationBar
                showBackButton
                showForwardButton
                showRefreshButton
                showHomeButton
                showUrlBar
                homeRoute="/"
                position="relative"
                theme="auto"
              />
            </div>
          )}
        </div>
      </NavigationProvider>
    </AppHeaderProvider>
  );
}
